/* eslint-disable max-len */
import { DEFAULT_COUNTRY_CODE } from '@rentguru/commons-utils';
import isNil from 'lodash/isNil';
import { ChevronDownIcon, GlobeIcon } from 'lucide-react';
import { type ComponentProps, forwardRef, type ReactNode, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import PhoneNumberInput, { type Country, getCountryCallingCode, type Labels } from 'react-phone-number-input';

import { Button, ButtonVariant } from '@ui/components/base/button';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxSeparator,
  ComboboxTrigger,
  ComboboxValue,
} from '@ui/components/base/combobox';
import { Field, FieldError, FieldLabel } from '@ui/components/base/field';
import { Input } from '@ui/components/base/input';
import { ScrollArea } from '@ui/components/base/scroll-area';
import { CountryFlag, getCountryLabels } from '@ui/components/fields/countrySelectUtils';
import { cn } from '@ui/lib/utils';

const SEARCH_MESSAGE_ID = 'search';
const NO_RESULT_MESSAGE_ID = 'noResult';
const TRIGGER_CLASSNAME =
  'h-12 gap-1 rounded-s-lg rounded-e-none border-e-0 px-2.5 hover:bg-transparent focus:z-10 data-pressed:bg-transparent';
const SEARCH_INPUT_CLASSNAME =
  'rounded-none border-0 border-input px-0 py-2.5 shadow-none ring-0! outline-none! focus-visible:border-border focus-visible:ring-0 focus-visible:ring-offset-0';
const LIST_VIEWPORT_CLASSNAME =
  'flex max-h-[min(var(--available-height),24rem)] w-full scroll-pt-2 scroll-pb-2 flex-col overscroll-contain';
const SCROLL_AREA_CLASSNAME =
  'size-full min-h-0 **:data-[slot=scroll-area-scrollbar]:m-0 [&_[data-slot=scroll-area-viewport]]:h-full [&_[data-slot=scroll-area-viewport]]:overscroll-contain';
const INVALID_TRIGGER_CLASSNAME =
  '[&_[data-slot=combobox-trigger]]:border-destructive [&_[data-slot=combobox-trigger]]:ring-3 [&_[data-slot=combobox-trigger]]:ring-destructive/20';
const PhoneNumberField = forwardRef<HTMLInputElement, ComponentProps<'input'>>(({ className, ...props }, ref) => (
  <Input ref={ref} className={cn('rounded-s-none focus-visible:z-10', className)} {...props} />
));
PhoneNumberField.displayName = 'PhoneNumberField';

interface CountryOption {
  label: string;
  value: Country;
}

interface CountrySelectProps {
  disabled?: boolean;
  value?: Country;
  options: { label: string; value?: Country }[];
  onChange: (country?: Country) => void;
}

const CountrySelect = ({ disabled, value: selectedCountry, options, onChange }: CountrySelectProps) => {
  const { formatMessage } = useIntl();
  const [searchValue, setSearchValue] = useState<string>('');

  const countries = useMemo<CountryOption[]>(
    () =>
      options.reduce<CountryOption[]>((acc, { label, value }) => {
        if (isNil(value)) {
          return acc;
        }
        acc.push({ label, value });
        return acc;
      }, []),
    [options]
  );

  const filteredCountries = useMemo<CountryOption[]>(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();
    if (normalizedSearch === '') {
      return countries;
    }
    return countries.filter(({ label }) => label.toLowerCase().includes(normalizedSearch));
  }, [countries, searchValue]);

  return (
    <Combobox
      items={filteredCountries}
      value={selectedCountry ?? null}
      onValueChange={(country: Country | null) => {
        if (!isNil(country)) {
          onChange(country);
        }
      }}
    >
      <ComboboxTrigger
        render={
          <Button
            variant={ButtonVariant.OUTLINE}
            className={cn(TRIGGER_CLASSNAME, disabled && 'opacity-50')}
            disabled={disabled}
          >
            <span className="sr-only">
              <ComboboxValue />
            </span>
            {!isNil(selectedCountry) && <CountryFlag country={selectedCountry} countryName={selectedCountry} />}
            {isNil(selectedCountry) && <GlobeIcon className="size-4 opacity-60" />}
            <ChevronDownIcon className="size-3.5 opacity-60" />
          </Button>
        }
      />
      <ComboboxContent className="w-xs *:data-[slot=input-group]:bg-transparent">
        <ComboboxInput
          placeholder={formatMessage({ id: SEARCH_MESSAGE_ID })}
          value={searchValue}
          onChange={(event) => setSearchValue(event.target.value)}
          showTrigger={false}
          className={SEARCH_INPUT_CLASSNAME}
        />
        <ComboboxSeparator />
        <ComboboxEmpty className="px-4 py-2.5 text-sm">{formatMessage({ id: NO_RESULT_MESSAGE_ID })}</ComboboxEmpty>
        <ComboboxList>
          <div className="relative flex max-h-full">
            <div className={LIST_VIEWPORT_CLASSNAME}>
              <ScrollArea className={SCROLL_AREA_CLASSNAME}>
                {filteredCountries.map(({ label, value }) => (
                  <ComboboxItem key={value} value={value} className="flex items-center gap-2">
                    <CountryFlag country={value} countryName={label} />
                    <span className="flex-1 text-sm">{label}</span>
                    <span className="text-sm text-foreground/50">{`+${getCountryCallingCode(value)}`}</span>
                  </ComboboxItem>
                ))}
              </ScrollArea>
            </div>
          </div>
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  label?: ReactNode;
  error?: string;
  invalid?: boolean;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  defaultCountry?: Country;
  onCountryChange?: (country: Country | undefined) => void;
  className?: string;
  fieldClassName?: string;
}

const PhoneInput = ({
  value,
  onChange,
  label,
  error,
  invalid,
  disabled,
  placeholder,
  id,
  defaultCountry = DEFAULT_COUNTRY_CODE,
  onCountryChange,
  className,
  fieldClassName,
}: PhoneInputProps) => {
  const { locale } = useIntl();
  const isInvalid = invalid || Boolean(error);
  const countryLabels = useMemo<Labels>(() => getCountryLabels(locale), [locale]);

  return (
    <Field className={fieldClassName}>
      {label && <FieldLabel htmlFor={id}>{label}</FieldLabel>}
      <PhoneNumberInput
        id={id}
        className={cn('flex', isInvalid && INVALID_TRIGGER_CLASSNAME, className)}
        international
        withCountryCallingCode
        addInternationalOption={false}
        smartCaret={false}
        labels={countryLabels}
        defaultCountry={defaultCountry}
        onCountryChange={onCountryChange}
        flagComponent={CountryFlag}
        countrySelectComponent={CountrySelect}
        inputComponent={PhoneNumberField}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete="tel"
        aria-invalid={isInvalid}
        value={value === '' ? undefined : value}
        onChange={(newValue) => onChange(newValue ?? '')}
      />
      {error && <FieldError>{error}</FieldError>}
    </Field>
  );
};

export { PhoneInput };
export type { Country as PhoneCountry, PhoneInputProps };
